import type { DayId } from '../calendar.ts'
import type { Moment } from '../events.ts'
import type { FamilyState, Task } from '../state.ts'
import { routinesOf, taskDayOf } from '../state.ts'
import type { MemberId } from '../ids.ts'

/**
 * Qué está sin hacer y ya tocaba.
 *
 * Una notificación es un instante: se ve o no se ve. Lo que hace que un niño
 * empiece solo no es el instante, es que el **estado** esté a la vista — que
 * quien coja la tablet a las nueve y media lo lea sin tener que acordarse de
 * nada, y que desaparezca cuando esté hecho.
 *
 * Por eso esto vive en el dominio y no en el servidor: el aviso en pantalla
 * tiene que funcionar en modo avión, que es donde vive esta app. El aviso que
 * manda el servidor es la copia de fuera, redundante y prescindible.
 *
 * Y es una función pura de (estado, día, minuto del día). Sin reloj dentro, así
 * que se puede probar a las nueve de la noche de cualquier día sin esperar.
 */

/** Qué rutina le corresponde a una hora del día. */
export function momentAtHour(hour: number): Moment {
  if (hour < 13) return 'morning'
  if (hour < 20) return 'afternoon'
  return 'evening'
}

export interface Reminder {
  readonly hora: string
  readonly previo: boolean
}

/** "21:00" a minutos desde medianoche. Devuelve null si no es una hora. */
export function minutesOf(hora: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hora)
  if (!match) return null
  return Number(match[1]) * 60 + Number(match[2])
}

/**
 * Las rutinas de un niño que ya tocaban y siguen sin validar.
 *
 * «Ya tocaban» es que ha pasado la hora que un adulto puso. Sin hora puesta no
 * hay nada pendiente: el aviso solo existe si alguien lo acordó, igual que
 * todo lo demás en esta casa.
 *
 * Una rutina marcada y esperando a un adulto **no cuenta como pendiente**. El
 * niño ya hizo lo suyo, y seguir enseñándole el aviso sería culparle de que su
 * padre no haya mirado todavía.
 */
export function overdueRoutines(
  state: FamilyState,
  memberId: MemberId,
  day: DayId,
  minutesNow: number,
  reminders: readonly Reminder[],
): readonly Task[] {
  const momentos = new Set<Moment>()
  for (const reminder of reminders) {
    const minutos = minutesOf(reminder.hora)
    if (minutos === null || minutos > minutesNow) continue
    momentos.add(momentAtHour(Math.floor(minutos / 60)))
  }

  const pendientes: Task[] = []
  for (const moment of momentos) {
    for (const task of routinesOf(state, memberId, moment)) {
      if (taskDayOf(state, day, memberId, task.id).status === 'pending') pendientes.push(task)
    }
  }
  return pendientes
}
